import { Component, inject, OnInit } from '@angular/core'; // Import necessary Angular core modules
// Import CommonModule for common directives
import { ActivatedRoute, Router } from '@angular/router';
import { CbAlertBannerModule, CbButtonModule, CbModalModule, CbSpinnerModule } from '@chubb/ui-components';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { Subscription } from 'rxjs';
import { PolicyMediator } from '../../../http-services/mediators/policy.mediator';
import { Feedback } from '../../../interfaces/feedback.model';
import { PolicyData } from '../../../interfaces/policy-data.model';
import { BannerAlertType } from '../../../shared/enums/alert-icon.enum';
import { QfaEventType } from '../../../shared/enums/event-type.enum';
import { AuthService } from '../../../shared/services/auth.service';
import { DataService } from '../../../shared/services/data.service';
import { LoggingService } from '../../../shared/services/logging.service';
import { MessageBannerService } from '../../../shared/services/message-banner.service';
import { AlertBannerComponent } from '../../widgets/alert-banner/alert-banner.component';
import { PolicyNotFoundComponent } from '../../widgets/policy-not-found/policy-not-found.component';
import { TotalPremiumComponent } from '../../widgets/total-premium/total-premium.component';
import { WidgetsModule } from '../../widgets/widgets.module'; // Import WidgetsModule for widget components

@Component({
  selector: 'app-coverage-screen', // Declare the component as a standalone component
  imports: [
    WidgetsModule,
    CbAlertBannerModule,
    CbButtonModule,
    CbSpinnerModule,
    ProgressSpinnerModule,
    CbModalModule,
    PolicyNotFoundComponent,
    TotalPremiumComponent,
    AlertBannerComponent,
  ], // Import other modules this component depends on
  templateUrl: './coverage-screen.component.html', // Define the path to the component template
  styleUrls: ['./coverage-screen.component.scss'],
})
export class CoverageScreenComponent implements OnInit {
  protected readonly _route: ActivatedRoute = inject(ActivatedRoute);
  protected readonly _mediator: PolicyMediator = inject(PolicyMediator);
  protected readonly _dataService: DataService = inject(DataService);
  protected readonly _oauthService: AuthService = inject(AuthService);
  protected readonly _loggingService: LoggingService = inject(LoggingService);
  protected readonly _router: Router = inject(Router);
  protected readonly _alertBanner: MessageBannerService = inject(MessageBannerService);

  protected _alertBannerSubscription: Subscription = new Subscription();
  protected _alertBannerMessageSubscription: Subscription = new Subscription();

  policyNotFound: boolean = false;
  loading: boolean = true;
  policyData?: PolicyData;
  openPolicyAlert: boolean = false;
  policyAlertMessage: Feedback = {};
  alertBannerType = BannerAlertType;

  ngOnInit() {
    if (this._oauthService.isLoggedIn()) {
      this._alertBannerSubscription = this._alertBanner.displayBanner$.subscribe((show: boolean) => {
        this.openPolicyAlert = show;
      });
      this._alertBannerMessageSubscription = this._alertBanner.displayBannerMessage$.subscribe((alert: Feedback) => {
        this.policyAlertMessage = alert ?? {};
      });
      this._route.queryParams.subscribe(params => {
        const policyNumber = params['policyNumber'] ?? '';
        if (policyNumber) {
          this._fetchPolicy(policyNumber); // Fetch data using the policy number
        }
      });
    }
  }

  ngOnDestroy() {
    if (this._alertBannerSubscription) {
      this._alertBannerSubscription.unsubscribe();
    }
    if (this._alertBannerMessageSubscription) {
      this._alertBannerMessageSubscription.unsubscribe();
    }
  }

  private _fetchPolicy(policyNumber: string): void {
    this._mediator
      .getPolicyData(policyNumber)
      .then((response: PolicyData) => {
        this._loggingService.logEvent(
          QfaEventType.INFO,
          `FRONTEND.CoverageScreenComponent.fetchPolicy - policyNumber: ${policyNumber}`,
        );
        if (!response) {
          this._loggingService.logEvent(
            QfaEventType.WARNING,
            `FRONTEND.CoverageScreenComponent.fetchPolicy - error receiving data: ${response}`,
          );
          this.policyNotFound = true;
        } else {
          this.policyData = response;
          this._dataService.set('originalLoadedQuote', response);
          this._dataService.updateData(response);
          this.policyNotFound = false;
        }
      })
      .catch(error => {
        this.handleError(error, policyNumber); // Call an async method
      })
      .finally(() => {
        this.loading = false;
      });
  }

  // Helper method that handles errors asynchronously
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async handleError(error: any, policyNumber: string): Promise<void> {
    if (error.status === 400 || error.status === 401) {
      // Token might be expired or invalid
      const loginSuccess = await this._oauthService.tryReauthenticate(
        'FRONTEND.CoverageScreenComponent.fetchPolicy - error: tryReauthenticate failed',
      );
      if (loginSuccess) {
        // Retry fetch after reauth
        setTimeout(() => this._fetchPolicy(policyNumber), 0);
      } else {
        this._router.navigate(['/access-denied']);
      }
    } else if (error.status === 404) {
      this.policyNotFound = true;
    } else {
      this._router.navigate(['/unknown-error']);
    }
  }

  closeAlert() {
    this._alertBanner.hideBanner();
  }
}
